import { Close } from '@mui/icons-material'
import { Components, Theme } from '@mui/material'

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    neutral: true
    white: true
    bronze: true
    gold: true
    orange: true
    lime: true
    mint: true
    pink: true
    ai: true
    default: false
    primary: false
    secondary: false
  }

  interface ChipPropsVariantOverrides {
    selection: true
    filled: false
    // TODO - disable once migration complete
    // outlined: false
  }

  interface ChipPropsSizeOverrides {
    // Legacy - supported as they may be in use historically, but deprecated
    tiny: true
    large: true
    // TODO - disable once migration complete
    // small: false
  }
}

const clickableHover = (backgroundColor: string, borderColor: string) => ({
  '&.MuiChip-clickable:hover': { backgroundColor, borderColor },
})

export const MuiChip: Components<Omit<Theme, 'components'>>['MuiChip'] = {
  defaultProps: {
    color: 'neutral',
    size: 'medium',
    // TODO - new icon
    deleteIcon: <Close />,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['radius-sm'],
      padding: theme.spacing(0.5, 2, 0.5, 1),
      height: 24,
      borderStyle: 'solid',
      borderWidth: theme.strokeWidth.default,
    }),
    label: ({ theme }) => ({
      ...theme.typography['small-medium'],
      padding: 0,
      paddingLeft: theme.spacing(1),
    }),
    icon: ({ theme }) => ({
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
      '&.OmuiCountBadge-root': {
        marginLeft: 0,
      },
      '&:not(.OmuiCountBadge-root)': {
        color: 'inherit',
        height: 14,
        width: 14,
      },
    }),
    deleteIcon: ({ theme }) => ({
      fontSize: 18,
      margin: theme.spacing(0.5),
      color: theme.palette.fg.muted,
      '&:hover': {
        color: theme.palette.fg.default,
      },
    }),
  },
  variants: [
    // Selection variant
    {
      props: { variant: 'selection' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.interactive.hover,
        // Explicit text colour — without it, MUI's built-in styling for the
        // legacy "neutral" palette colour applies its white contrastText
        color: theme.palette.text.default,
        border: 'none',
        padding: theme.spacing(0.5),
        '& .MuiChip-label': {
          ...theme.typography['body-regular'],
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(0.5),
        },
        '&:not(:has(.MuiChip-deleteIcon)) .MuiChip-label': {
          paddingRight: theme.spacing(1.5),
        },
        '&.MuiChip-clickable:hover': {
          backgroundColor: theme.palette.bg.interactive.active,
        },
      }),
    },
    // Colours
    {
      // The variant check stops the neutral color overriding selection chips,
      // which set their own background.
      props: (props) => props.variant !== 'selection' && props.color === 'neutral',
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.neutral.muted,
        borderColor: theme.palette.border.subtle,
        color: theme.palette.text.default,
        ...clickableHover(theme.palette.bg.neutral.mutedHover, theme.palette.border.strong),
      }),
    },
    {
      props: { color: 'white' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.surface.elevated,
        borderColor: theme.palette.border.subtle,
        color: theme.palette.text.default,
        ...clickableHover(theme.palette.bg.surface.elevated, theme.palette.border.strong),
      }),
    },
    {
      props: { color: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.success.muted,
        borderColor: theme.palette.border.success.subtle,
        color: theme.palette.text.success.onSuccessMuted,
        ...clickableHover(theme.palette.bg.success.mutedHover, theme.palette.border.success.strong),
      }),
    },
    {
      props: { color: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.warning.muted,
        borderColor: theme.palette.border.warning.subtle,
        color: theme.palette.text.warning.onWarningMuted,
        ...clickableHover(theme.palette.bg.warning.mutedHover, theme.palette.border.warning.strong),
      }),
    },
    {
      props: { color: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.muted,
        borderColor: theme.palette.border.danger.subtle,
        color: theme.palette.text.danger.onDangerMuted,
        ...clickableHover(theme.palette.bg.danger.mutedHover, theme.palette.border.danger.strong),
      }),
    },
    {
      props: { color: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.info.muted,
        borderColor: theme.palette.border.info.subtle,
        color: theme.palette.text.info.onInfoMuted,
        ...clickableHover(theme.palette.bg.info.mutedHover, theme.palette.border.info.strong),
      }),
    },
    {
      props: { color: 'ai' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.ai.muted,
        borderColor: theme.palette.border.ai.subtle,
        color: theme.palette.text.ai.default,
        ...clickableHover(theme.palette.bg.ai.mutedHover, theme.palette.border.ai.strong),
      }),
    },
    {
      props: { color: 'bronze' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.bronze.muted,
        borderColor: theme.palette.border.utility.bronze.muted,
        color: theme.palette.text.utility.bronze.default,
        ...clickableHover(theme.palette.bg.utility.bronze.mutedHover, theme.palette.border.utility.bronze.strong),
      }),
    },
    {
      props: { color: 'gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.gold.muted,
        borderColor: theme.palette.border.utility.gold.muted,
        color: theme.palette.text.utility.gold.default,
        ...clickableHover(theme.palette.bg.utility.gold.mutedHover, theme.palette.border.utility.gold.strong),
      }),
    },
    {
      props: { color: 'orange' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.orange.muted,
        borderColor: theme.palette.border.utility.orange.muted,
        color: theme.palette.text.utility.orange.default,
        ...clickableHover(theme.palette.bg.utility.orange.mutedHover, theme.palette.border.utility.orange.strong),
      }),
    },
    {
      props: { color: 'lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.lime.muted,
        borderColor: theme.palette.border.utility.lime.muted,
        color: theme.palette.text.utility.lime.default,
        ...clickableHover(theme.palette.bg.utility.lime.mutedHover, theme.palette.border.utility.lime.strong),
      }),
    },
    {
      props: { color: 'mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.mint.muted,
        borderColor: theme.palette.border.utility.mint.muted,
        color: theme.palette.text.utility.mint.default,
        ...clickableHover(theme.palette.bg.utility.mint.mutedHover, theme.palette.border.utility.mint.strong),
      }),
    },
    {
      props: { color: 'pink' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.pink.muted,
        borderColor: theme.palette.border.utility.pink.muted,
        color: theme.palette.text.utility.pink.default,
        ...clickableHover(theme.palette.bg.utility.pink.mutedHover, theme.palette.border.utility.pink.strong),
      }),
    },
  ],
}
